import React, { Fragment, useEffect } from 'react';
import _ from 'lodash';
import { Box } from '@nerdwallet/currency/components/Box';
import {
  ControlledTabs,
  useTabState,
  type ControlledTabsProps,
} from '@nerdwallet/currency/components/Tabs';
import { Stack } from '@nerdwallet/currency/components/Stack';
import { Text } from '@nerdwallet/currency/components/Text';

import styles from './breakdown.module.less';

type BreakDownContent = {
  label: string;
  /**
   * There're some scenarios where we want to show a different label on mobile
   */
  mobileLabel?: React.ReactNode;
  /**
   * There are some scenarios where we don't want to show any label on mobile
   */
  hideLabelOnMobile?: boolean;
  content: React.ReactNode;
  id?: string;
  /** Legacy events */
  trackingProps?: Record<string, any>;
};

export interface BreakdownProps {
  content: BreakDownContent[];
  /** Callback to be called when selected tab changes */
  onSectionEnter?: (index: number) => void;
  /** Callback to be called when selected tab changes */
  onSectionChange?: (index: number) => void;
  /** Configuration for legacy event tracking. */
  tabsTracking?: ControlledTabsProps['tracking'];
  /**
   * The index of the tab that should be initially selected
   */
  defaultTabIndex?: number;
  className?: string;
}

const Breakdown: React.FC<BreakdownProps> = ({
  content,
  tabsTracking,
  defaultTabIndex,
  onSectionEnter,
  onSectionChange,
  className,
}) => {
  const tabState = useTabState({
    tabs: content,
    defaultTabIndex,
  });

  useEffect(() => {
    if (onSectionEnter) onSectionEnter(tabState.selectedIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabState.selectedIndex]);

  return (
    <Box className={className}>
      <Box className={styles.desktop} paddingBottom={2}>
        <ControlledTabs
          onChange={onSectionChange}
          tabState={tabState}
          tracking={tabsTracking}
        />
      </Box>
      <Box className={styles.mobile}>
        <Stack spacing={4}>
          {content?.map((item) => {
            const labelOnMobile = item.mobileLabel ?? item.label;

            return (
              <Fragment key={_.kebabCase(item.label)}>
                <Stack spacing={2}>
                  {!item.hideLabelOnMobile && <Text bold>{labelOnMobile}</Text>}
                  {item.content}
                </Stack>
              </Fragment>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default Breakdown;
